/**
 * The `[prefix-]number` + 4-digit bank code notation that CZ and SK share.
 * @module banking/schemes/domestic-account
 */

import { parseCzechBankAccount, type BankingValidationError } from '../validation';
import type { BankAccount } from './bank-account-scheme';
import { compact, countryMismatchErrors, REQUIRED_ERRORS } from './scheme-helpers';

/** A domestic account with leading zeros removed and a four-digit bank code. */
export interface DomesticAccountParts {
  prefix: string | null;
  number: string;
  bankCode: string;
}

/** Sources drop the leading zero of a bank code (`300`); padding is unambiguous. */
export function normalizeBankCode(value?: string | null): string | null {
  const digits = compact(value);
  return digits && /^\d{1,4}$/.test(digits) ? digits.padStart(4, '0') : null;
}

export function stripLeadingZeros(value: string): string {
  return value.replace(/^0+/, '') || '0';
}

export function parseDomesticAccount(account: BankAccount): DomesticAccountParts | null {
  const bankCode = normalizeBankCode(account.bankIdentifier);
  const number = compact(account.accountNumber);
  if (!bankCode || !number) return null;

  const parts = parseCzechBankAccount(`${number}/${bankCode}`);
  if (!parts) return null;

  const prefix = stripLeadingZeros(parts.prefix ?? '0');
  return {
    prefix: prefix === '0' ? null : prefix,
    number: stripLeadingZeros(parts.accountNumber).padStart(2, '0'),
    bankCode
  };
}

export function formatDomesticNumber(parts: DomesticAccountParts): string {
  return parts.prefix ? `${parts.prefix}-${parts.number}` : parts.number;
}

/** Required fields, country and format; the parts when all of them hold. */
export function checkDomesticAccount(
  account: BankAccount,
  countryCode: string
): { errors: BankingValidationError[]; parts: DomesticAccountParts | null } {
  const errors: BankingValidationError[] = [];
  if (!compact(account.accountNumber)) errors.push(REQUIRED_ERRORS.accountNumber);
  if (!compact(account.bankIdentifier)) errors.push(REQUIRED_ERRORS.bankIdentifier);
  errors.push(...countryMismatchErrors(account.countryCode, countryCode));
  if (errors.length > 0) return { errors, parts: null };

  if (!normalizeBankCode(account.bankIdentifier)) {
    errors.push({ code: 'bankIdentifierInvalid', message: 'Bank code must be 1 to 4 digits' });
    return { errors, parts: null };
  }

  const parts = parseDomesticAccount(account);
  if (!parts) {
    errors.push({
      code: 'invalidFormat',
      message:
        'Invalid bank account format (expected [prefix-]accountNumber and a 4-digit bank code)'
    });
  }

  return { errors, parts };
}
